import React, { useReducer, useEffect, useState } from "react";

import { CloseCircle, Info } from "icons";
import { isNil } from "ramda";

import cdesApi from "apis/cdeDefinitions";
import multiSourceExceptionProfilingReportApi from "apis/MultiSourceExceptionProfilingReport";
import {
  SubHeader,
  Select,
  Table,
  Typography,
  Tooltip,
  Pagination,
  PageLoader,
  SearchBar,
  Button,
  Accordion,
} from "clearsense-ui/components";
import {
  multiSourceDashboardReducer,
  INITIAL_STATE,
  ACTIONS,
} from "reducers/multiSourceDashboard";
import moveToStart from "utils/moveToStart";

import Filter from "./Filter";
import { isColumnDynamicHeader } from "./utils";

const MultiSourceExceptionProfilingReport = () => {
  const [state, dispatch] = useReducer(
    multiSourceDashboardReducer,
    INITIAL_STATE
  );
  const [filters, setFilters] = useState("");
  const [sort, setSort] = useState("");
  const [prevFilters, setPrevFilters] = useState({ conditions: [] });

  const getPivotedData = async ({ cde, search, page, sort, filter }) => {
    if (!cde) {
      return;
    }
    dispatch({ type: ACTIONS.SET_PIVOTED_TABLE, payload: { isLoading: true } });
    try {
      const { data } = await multiSourceExceptionProfilingReportApi.show(
        encodeURIComponent(cde.label),
        isNil(page) ? state?.pagination?.currentPage : page,
        state?.pagination?.pageSize,
        isNil(search) ? state?.npiSearch : search,
        sort,
        filter
      );
      const headings = data.headings?.map(heading => ({
        dataIndex: heading.label,
        key: heading.value,
        title: heading.label,
        sorter: true,
      }));
      dispatch({
        type: ACTIONS.SET_PIVOTED_TABLE,
        payload: { headings, count: data.total_count, rows: data.table },
      });
    } catch (e) {
      logger.error(e);
    } finally {
      dispatch({
        type: ACTIONS.SET_PIVOTED_TABLE,
        payload: { isLoading: false },
      });
      dispatch({
        type: ACTIONS.SET_PIVOTED_DATA_SECTION_LOADING,
        payload: false,
      });
    }
  };

  const handleTableChange = sorter => {
    const sorterField = isColumnDynamicHeader(sorter.field)
      ? "context_value"
      : sorter.field;
    const sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";

    if (!sorter.order) {
      setSort("");
      getPivotedData({
        cde: state?.selectedCde,
        page: 1,
        sort: "",
        filter: filters,
      });
    } else {
      setSort(`ORDER BY "${sorterField}" ${sortOrder}`);
      getPivotedData({
        cde: state?.selectedCde,
        page: 1,
        sort: `ORDER BY "${sorterField}" ${sortOrder}`,
        filter: filters,
      });
    }
  };

  const getAggregatedTable = async cde => {
    try {
      dispatch({
        type: ACTIONS.SET_AGGREGATED_TABLE,
        payload: { isLoading: true },
      });
      const { data } =
        await multiSourceExceptionProfilingReportApi.get_aggregated_view(
          encodeURIComponent(cde.label, "UTF-8")
        );
      const headings = data.headings.map(heading =>
        heading.key === "first"
          ? {
              ...heading,
              render: (text, record) => (
                <div className="flex justify-between items-center">
                  <Typography
                    type="semibold"
                    className="text-content-darker text-xs"
                  >
                    {text}
                  </Typography>
                  <Tooltip content={record.tooltip} position="right">
                    <div>
                      <Info size={12} />
                    </div>
                  </Tooltip>
                </div>
              ),
              title: "",
            }
          : heading
      );
      dispatch({
        type: ACTIONS.SET_AGGREGATED_TABLE,
        payload: {
          headings: moveToStart(headings, heading => heading.title === ""),
          rows: data.table,
        },
      });
    } catch (e) {
      logger.error(e);
    } finally {
      dispatch({
        type: ACTIONS.SET_AGGREGATED_TABLE,
        payload: { isLoading: false },
      });
    }
  };

  const handleCdeSelected = cde => {
    dispatch({
      type: ACTIONS.SET_PIVOTED_TABLE,
      payload: { rows: null, headings: null },
    });
    dispatch({
      type: ACTIONS.SET_PIVOTED_DATA_SECTION_LOADING,
      payload: true,
    });
    getAggregatedTable(cde);
    getPivotedData({ cde });
    dispatch({ type: ACTIONS.SET_SELECTED_CDES, payload: cde });
  };

  const handleNpiSearch = () => {
    dispatch({
      type: ACTIONS.SET_PAGINATION,
      payload: { currentPage: 1 },
    });
    getPivotedData({ cde: state?.selectedCde, page: 1, sort, filter: filters });
  };

  const clearSearch = () => {
    dispatch({ type: ACTIONS.SET_NPI_SEARCH, payload: "" });
    getPivotedData({
      cde: state?.selectedCde,
      search: "",
      sort,
      filter: filters,
    });
  };

  useEffect(() => {
    state?.selectedCde && getPivotedData({ cde: state?.selectedCde });
  }, [state?.pagination.currentPage, state?.pagination?.pageSize]);

  useEffect(() => {
    const getCDEs = async () => {
      const { data } = await cdesApi.fetchCdes("", 1, 999);

      const cdeData = data.cde_definitions.cdes
        .map(cde => ({
          value: cde.id,
          label: cde.name,
        }))
        .sort((a, b) => {
          if (a.label > b.label) return 1;

          if (a.label < b.label) return -1;

          return 0;
        });
      dispatch({ type: ACTIONS.SET_CDES, payload: cdeData });
    };
    getCDEs();
  }, []);

  return (
    <div className="multi-source-report__wrapper p-4">
      <SubHeader
        breadcrumbs={[
          { label: "Multi-Source Exception Profiling Report", link: "#" },
        ]}
        className="p-4"
      />
      <Select
        size="small"
        name="cde"
        label="Critical Data Element"
        placeholder="Select a Critical Data Element"
        options={state.cdes}
        isDisabled={
          !state.cdes ||
          state?.pivotedTable?.isLoading ||
          state?.isPivotedDataSectionLoading
        }
        isLoading={!state.cdes}
        className="w-1/3 ml-4 mt-4 mb-8"
        onChange={handleCdeSelected}
      />
      {state?.aggregatedTable?.headings && (
        <Accordion multipleExpanded defaultActiveKey={[0, 1]} className="ml-4">
          <Accordion.Item
            title={
              <Typography style="h4" type="semibold" className="ml-4">
                Aggregated Data
              </Typography>
            }
          >
            <div className="ml-4 mt-4">
              {state?.aggregatedTable.rows ? (
                <Table
                  columnData={state?.aggregatedTable?.headings}
                  rowData={state?.aggregatedTable?.rows}
                  isLoading={state?.aggregatedTable?.isLoading}
                  className="ml-4"
                />
              ) : (
                <div className="h-28 grid place-items-center">
                  <Typography type="semibold">
                    No data for selected CDE
                  </Typography>
                </div>
              )}
            </div>
          </Accordion.Item>
          <Accordion.Item
            title={
              <Typography style="h4" type="semibold" className="ml-4">
                Pivoted Data
              </Typography>
            }
          >
            {state?.isPivotedDataSectionLoading && (
              <div className=" h-28">
                <PageLoader />
              </div>
            )}
            {state?.aggregatedTable?.headings &&
              !state?.isPivotedDataSectionLoading && (
                <div className="ml-4 mt-4">
                  <div className="flex justify-between">
                    <div className="flex gap-2 mb-4 w-80">
                      <SearchBar
                        placeholder={`Enter ${
                          state?.pivotedTable?.headings?.[0].title || ""
                        }`}
                        size="large"
                        name="key"
                        value={state?.npiSearch}
                        onChange={e =>
                          dispatch({
                            type: ACTIONS.SET_NPI_SEARCH,
                            payload: e.target.value.trim(),
                          })
                        }
                        suffix={
                          state?.npiSearch && (
                            <div
                              onClick={clearSearch}
                              className="cursor-pointer pr-2"
                            >
                              <CloseCircle />
                            </div>
                          )
                        }
                      />
                      <Button label="Search" onClick={handleNpiSearch} />
                    </div>
                    <Filter
                      title="Pivoted Data"
                      state={state}
                      sort={sort}
                      columnOptions={state?.pivotedTable?.headings?.map(
                        column => ({
                          key: column?.key,
                          label: column?.title,
                          value: column?.key,
                        })
                      )}
                      prevFilters={prevFilters}
                      getPivotedData={getPivotedData}
                      setFilters={setFilters}
                      setPrevFilters={setPrevFilters}
                    />
                  </div>
                </div>
              )}

            {state?.aggregatedTable?.headings &&
              !state?.pivotedTable?.headings &&
              !state?.isPivotedDataSectionLoading &&
              !state?.pivotedTable?.isLoading && (
                <div className="h-28 grid place-items-center">
                  <Typography type="semibold">
                    No data for selected CDE
                  </Typography>
                </div>
              )}
            {state?.pivotedTable?.headings && (
              <div className="ml-4 mt-4 multi-source-report__pivoted-table">
                <Table
                  columnData={state?.pivotedTable?.headings}
                  rowData={state?.pivotedTable?.rows}
                  className="ml-4"
                  isLoading={state?.pivotedTable?.isLoading}
                  scroll={{ y: 300 }}
                  onChange={(_pagination, _filters, sorter) =>
                    handleTableChange(sorter)
                  }
                />
                <Pagination
                  className="px-6 mt-4"
                  totalRecordsCount={state?.pivotedTable?.count}
                  currentPage={state?.pagination?.currentPage}
                  setCurrentPage={currentPage =>
                    dispatch({
                      type: ACTIONS.SET_PAGINATION,
                      payload: { currentPage },
                    })
                  }
                  pageSize={state?.pagination?.pageSize}
                  setPageSize={pageSize =>
                    dispatch({
                      type: ACTIONS.SET_PAGINATION,
                      payload: { pageSize },
                    })
                  }
                  pageSizeOptionsList={[10, 20, 25, 35, 45, 50]}
                  showResultsPerPage
                />
              </div>
            )}
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );
};

export default MultiSourceExceptionProfilingReport;
